import React, { useState } from "react";
import QRCode from "react-qr-code";
import { useLocation, useParams } from "react-router-dom";
import { formatAmount, truncateWordd } from "../../utils/wordFunctions";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import GetRates from "../../components/utils/GetRates";
import { FiCopy } from "react-icons/fi";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constant/api";
import { useMutation } from "@tanstack/react-query";
import { errorMessage } from "../../utils/errorMessage";
import { ClipLoader } from "react-spinners";
import Success from "./Success";
import GetTickers from "../../components/utils/GetTickers";
import PoweredByDeex from "../../components/PoweredByDeex";
import PageAvatar from "../../components/PageAvatar";
import { depCoins, getNetworkLabel } from "../../constant";

const DepsitPage = () => {
  const location = useLocation();
  const axiosInstance = useAuthAxios();
  const info = location?.state;
  const usdtRate = GetRates("USDT");
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();

  const sendDeets = async (data: any) => {
    const response = await axiosInstance.post(
      API.sendCustomerDeets(id ?? ""),
      data
    );
    return response.data;
  };

  const completeSendDeets = useMutation({
    mutationFn: sendDeets,
    onSuccess: (r) => {
      toast.success(`Transaction sent...`);
      setTimeout(() => {
        if (info?.redirectLink) {
          let link = info.redirectLink;
          if (!link.startsWith("http://") && !link.startsWith("https://")) {
            link = "https://" + link;
          }
          window.open(link, "_blank"); // Opens in a new tab
        } else {
          setOpenSuccess(true);
        }
      }, 1500);
    },
    onError: (e) => {
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  const { data: coinRates, isLoading } = GetTickers();

  /** Resolve a coin's ticker rate, trying the coin code and then any aliases. */
  const getCoinRate = (coinName: string) => {
    if (!coinRates) return undefined;
    if (coinRates[coinName] != null) return coinRates[coinName];
    const coin = depCoins.find((c: any) => c.abbr === coinName);
    if (coin?.aliases) {
      for (const alias of coin.aliases) {
        if (coinRates[alias] != null) return coinRates[alias];
      }
    }
    return undefined;
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#F1F3F5] font-manrope">
      <div className="flex flex-1 justify-center px-4 py-8 mds:py-12">
        <div className="w-full max-w-[460px]">
          {/* Merchant identity */}
          <div className="flex flex-col items-center text-center">
            <PageAvatar
              name={info?.name}
              image={info?.thumbnailUrl}
              className="h-[92px] w-[92px] shadow-md"
              textClassName="text-[32px]"
            />
            <h4 className="mt-4 font-sora text-[18px] font-bold text-gray-900">
              {info?.name}
            </h4>
            <p className="mt-1 max-w-[340px] text-[13px] font-medium text-gray-500">
              {info?.description}
            </p>
          </div>

          {/* Payment card */}
          <div className="mt-8 rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.04)] md:p-6">
            {/* Amount hero */}
            <div className="rounded-xl bg-secondary_button/5 py-5 text-center">
              <h4 className="text-[13px] font-medium text-gray-500">
                You are to pay
              </h4>
              <div className="mt-1 font-sora text-[24px] font-bold text-secondary_button">
                {isLoading ? (
                  <div className="flex w-full items-center justify-center">
                    <ClipLoader color="#0B84CB" size={22} />
                  </div>
                ) : (
                  <h4>
                    {info?.amount
                      ? formatAmount(
                          Number(info?.amount) /
                            usdtRate /
                            (getCoinRate(info?.coinName) || 1)
                        )
                      : formatAmount(
                          Number(info?.paymentAmount) /
                            usdtRate /
                            (getCoinRate(info?.coinName) || 1)
                        )}{" "}
                    {info?.coinName}
                  </h4>
                )}
              </div>
            </div>

            {/* QR */}
            {info?.walletAddy && (
              <div className="mt-6 flex justify-center">
                <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                  <QRCode size={160} value={info?.walletAddy ?? ""} />
                </div>
              </div>
            )}

            <p className="mt-5 text-center text-[12px] font-medium text-gray-400">
              Scan the code or copy the address below to pay
            </p>

            {/* Network */}
            <div className="mt-4 flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[14px] font-semibold text-gray-600">
              <span className="text-gray-400">Network:</span>
              {getNetworkLabel(info?.networkName ?? "")}
            </div>

            {/* Address + copy */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex flex-1 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[14px] font-semibold text-gray-600">
                {truncateWordd(info?.walletAddy ?? "")}
              </div>
              <CopyToClipboard
                text={info?.walletAddy}
                onCopy={() => {
                  toast.success("Address Copied");
                }}
              >
                <button className="flex flex-none items-center gap-1.5 rounded-xl bg-[#D4EBFD] px-4 py-3 text-[14px] font-semibold text-secondary_button transition hover:bg-[#c3e2fb]">
                  <FiCopy />
                  Copy
                </button>
              </CopyToClipboard>
            </div>

            <button
              onClick={() => {
                if (info?.allowData) {
                  const data1 = {
                    fullName: info?.fullname,
                    email: info?.email,
                    phone: info?.phone,
                    amount: info?.amount
                      ? Number(info?.amount)
                      : info?.paymentAmount,
                  };
                  completeSendDeets.mutate(data1);
                } else {
                  setOpenSuccess(true);
                }
              }}
              className="mt-8 flex h-[52px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-semibold text-white transition hover:opacity-90"
            >
              {completeSendDeets?.isPending ? (
                <ClipLoader color="#FFFFFF" size={28} />
              ) : (
                "I have made this payment"
              )}
            </button>
          </div>

          <PoweredByDeex />
        </div>
      </div>
      {openSuccess && (
        <Success
          redirectLink={info?.redirectLink}
          setOpenSuccess={setOpenSuccess}
          id={id}
        />
      )}
    </div>
  );
};

export default DepsitPage;
